import { gql } from '@apollo/client';

export type PermissionCheckStatus =
  'ALLOWED' | 'DENIED' | 'NO_SCOPE' | 'UNAVAILABLE' | 'OPERATOR' | 'UNKNOWN_RESOURCE';

export interface PermissionCheck {
  resource: string;
  level: 'READ' | 'FULL';
  /** Null when Keystone never asked FGA; `status` says why. */
  allowed: boolean | null;
  status: PermissionCheckStatus;
}

/** Both levels of one resource in one round trip, so a screen knows whether to
 *  draw at all and whether to draw its write controls. */
export const RESOURCE_ACCESS_QUERY = gql`
  query ResourceAccess($resource: String!) {
    read: permissionCheck(resource: $resource, level: READ) {
      resource
      level
      allowed
      status
    }
    full: permissionCheck(resource: $resource, level: FULL) {
      resource
      level
      allowed
      status
    }
  }
`;

export interface ResourceAccessResult {
  read: PermissionCheck;
  full: PermissionCheck;
}

export interface ResourceAccessVariables {
  resource: string;
}

/** The brokerage keys a brokerage page draws from, asked at both levels. */
export const BROKERAGE_KEYS = ['members', 'access_control', 'organisation_config'] as const;

export type BrokerageKey = (typeof BROKERAGE_KEYS)[number];

const tenantCheck = (alias: string, resource: string, level: 'READ' | 'FULL') => `
    ${alias}: permissionCheck(resource: "${resource}", level: ${level}, tenantId: $tenantId) {
      resource
      level
      allowed
      status
    }`;

/** Every brokerage key at both levels for one brokerage, in one round trip. */
export const BROKERAGE_ACCESS_CHECK_QUERY = gql`
  query BrokerageAccessCheck($tenantId: UUID!) {
    ${BROKERAGE_KEYS.flatMap((key) => [
      tenantCheck(`${key}_read`, key, 'READ'),
      tenantCheck(`${key}_full`, key, 'FULL'),
    ]).join('\n')}
  }
`;

export type BrokerageAccessCheckResult = Record<string, PermissionCheck>;
